import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { Project, Customer } from '../types';
import { saveFiltersToStorage, loadFiltersFromStorage } from '../utils/filterStorage';
import Pagination from './Pagination';
import SearchableDropdown from './SearchableDropdown';

interface BaseItem {
  id: number;
  title: string;
}

interface ProjectWithCategory extends Omit<Project, 'category'> {
  category?: number | null;
}

const ProjectCategories: React.FC = () => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<ProjectWithCategory[]>([]);
  const [customers, setCustomers] = useState<BaseItem[]>([]);
  const [categories, setCategories] = useState<BaseItem[]>([]);
  const [statuses, setStatuses] = useState<BaseItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Load initial filters from localStorage
  const defaultFilters = {
    selectedCustomer: 0,
    selectedStatus: 10, // All
    selectedCategory: -2, // Without Categories
    searchText: ''
  };
  const initialFilters = loadFiltersFromStorage('project-categories', defaultFilters);

  // Filters
  const [selectedCustomer, setSelectedCustomer] = useState<number>(initialFilters.selectedCustomer);
  const [selectedStatus, setSelectedStatus] = useState<number>(initialFilters.selectedStatus);
  const [selectedCategory, setSelectedCategory] = useState<number | null>(initialFilters.selectedCategory);
  const [searchText, setSearchText] = useState<string>(initialFilters.searchText);

  const transactionCategories = useMemo(() => [
    { id: 0, title: "Income - Development" },
    { id: 1, title: "Income - Consulting" },
    { id: 2, title: "Income - Training" },
    { id: 3, title: "Income - Licensing" },
    { id: 4, title: "Income - Partnership" },
    { id: 5, title: "Income - Grants" },
    { id: 28, title: "Digital Product Revenue" }
  ], []);

  const filterCategories = [
    { id: -2, title: "Without Categories" },
    { id: -1, title: "All Categories" },
    ...transactionCategories
  ];


  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers');
      const customersData = response.data.data || response.data;
      setCustomers([
        { id: 0, title: "Customer" },
        ...customersData.map((c: Customer) => ({ id: c.id, title: c.title }))
      ]);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadStatuses = async () => {
    try {
      const response = await api.get('/statuses');
      const statusesData = response.data.data || response.data;

      setStatuses([
        { id: 10, title: "Status" },
        ...statusesData
      ]);
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        skip: currentPage * pageSize,
        take: pageSize
      };

      if (selectedCustomer !== 0) {
        params.customerId = selectedCustomer;
      }

      if (selectedStatus !== 10) {
        params.statusId = selectedStatus;
      }

      if (selectedCategory !== -1 && selectedCategory !== null) {
        if (selectedCategory === -2) {
          params.withoutCategory = true;
        } else {
          params.category = selectedCategory;
        }
      }

      if (searchText) {
        params.searchText = searchText;
      }

      const response = await api.get('/projects', { params });
      const projectsData = response.data.items ?? response.data;
      setProjects(projectsData);
      setTotalCount(response.data.totalCount ?? projectsData.length);
    } catch (error) {
      console.error('Error loading projects:', error);
      setProjects([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, selectedCustomer, selectedStatus, selectedCategory, searchText]);

  useEffect(() => {
    loadCustomers();
    setCategories(transactionCategories);
    loadStatuses();
  }, [transactionCategories]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-load projects when filters change
  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const saveCategory = async (project: ProjectWithCategory, newCategory: number | null) => {
    try {
      await api.put(`/projects/${project.id}/category`, { category: newCategory });
      
      // Update local state
      setProjects(prev => 
        prev.map(p => 
          p.id === project.id 
            ? { ...p, category: newCategory }
            : p
        )
      );
    } catch (error) {
      console.error('Error saving category:', error);
    }
  };


  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ marginBottom: '1rem' }}>
        <h1 style={{ 
          color: 'var(--text-primary)', 
          fontSize: 'var(--font-size-3xl)', 
          fontWeight: '700',
          marginBottom: '0.35rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          Categories
        </h1>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-lg)', margin: 0 }}>
          Manage project categories and assignments
        </p>
      </div>
      
      {loading ? (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          minHeight: '50vh',
          flexDirection: 'column',
          gap: '0.75rem'
        }}>
          <div className="spinner-border" role="status"></div>
          <p style={{ color: 'var(--text-muted)' }}>Loading projects...</p>
        </div>
      ) : (
        <div className="card">
          <div className="card-header">
           <div className="d-flex align-items-center gap-2 flex-wrap">
             <h5 className="card-title">Project Categories</h5>
            <div className="ms-auto d-flex flex-wrap gap-2 align-items-end" style={{ justifyContent: 'flex-end' }}>
              <div>
                <input
                  type="text"
                  className="form-control"
                  value={searchText}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    setSearchText(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('project-categories', {
                      selectedCustomer,
                      selectedStatus,
                      selectedCategory,
                      searchText: newValue
                    });
                  }}
                  placeholder="Search projects..."
                />
              </div>
              <div style={{ minWidth: '200px' }}>
                <SearchableDropdown
                  id="project-categories-customer"
                  label="Customer"
                  showLabel={false}
                  items={customers}
                  value={selectedCustomer}
                  onChange={(value) => {
                    const newValue = typeof value === 'string' ? parseInt(value, 10) : value;
                    setSelectedCustomer(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('project-categories', {
                      selectedCustomer: newValue,
                      selectedStatus,
                      selectedCategory,
                      searchText
                    });
                  }}
                  getItemValue={(item) => item.id}
                  getItemLabel={(item) => item.title}
                  placeholder="Customer"
                  searchPlaceholder="Search customers..."
                  size="sm"
                />
              </div>
              <div>
                <select
                  className="form-select"
                  value={selectedStatus}
                  onChange={(e) => {
                    const newValue = parseInt(e.target.value);
                    setSelectedStatus(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('project-categories', {
                      selectedCustomer,
                      selectedStatus: newValue,
                      selectedCategory,
                      searchText
                    });
                  }}
                >
                  {statuses.map(status => (
                    <option key={status.id} value={status.id}>
                      {status.title}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <select
                  className="form-select"
                  value={selectedCategory ?? -2}
                  onChange={(e) => {
                    const newValue = e.target.value === '' ? null : parseInt(e.target.value);
                    setSelectedCategory(newValue);
                    setCurrentPage(0);
                    saveFiltersToStorage('project-categories', {
                      selectedCustomer,
                      selectedStatus,
                      selectedCategory: newValue,
                      searchText
                    });
                  }}
                >
                  {filterCategories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        </div>
          <div className="card-body">
            <div>
              <table className="table table-hover">
                <thead>
                  <tr>
                    <th className="text-start" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      Project
                    </th>
                    <th className="text-start" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      Customer
                    </th>
                    <th className="text-start" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      Category
                    </th>
                    <th className="text-start" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      Status
                    </th>
                    <th className="text-start" style={{ color: 'var(--text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {projects.map((project) => (
                    <ProjectRow
                      key={project.id}
                      project={project}
                      categories={categories}
                      onSaveCategory={saveCategory}
                      navigate={navigate}
                    />
                  ))}
                </tbody>
              </table>

              {totalCount === 0 && !loading && (
                <div style={{
                  textAlign: 'center',
                  padding: '1.5rem',
                  color: 'var(--text-muted)'
                }}>
                  <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true"></i>
                  </div>
                  <h5>No projects found</h5>
                  <p>No projects found matching your criteria.</p>
                </div>
              )}

              {totalCount > 0 && (
                <Pagination
                  currentPage={currentPage}
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  onPageChange={setCurrentPage}
                  onPageSizeChange={setPageSize}
                  showPageSize={true}
                  pageSizeOptions={[10, 25, 50, 100]}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface ProjectRowProps {
  project: ProjectWithCategory;
  categories: BaseItem[];
  onSaveCategory: (project: ProjectWithCategory, category: number | null) => Promise<void>;
  navigate: ReturnType<typeof useNavigate>;
}

const ProjectRow: React.FC<ProjectRowProps> = ({ project, categories, onSaveCategory, navigate }) => {
  const [localCategory, setLocalCategory] = useState<number | null>(project.category ?? null);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSaveCategory(project, localCategory);
    } catch (error) {
      console.error('Error saving category:', error);
    } finally {
      setSaving(false);
    }
  };

  const getStatusText = (statusId: number) => {
    const status = {
      1: "Request",
      2: "Canceled", 
      3: "In Progress",
      4: "Completed",
      5: "Support",
      6: "Lead",
      7: "Opportunity",
      8: "Rejected",
      9: "Follow-up",
      10: "Confirmed",
      11: "Maintenance",
      12: "Lost",
      13: "Internal"
    }[statusId];
    return status || "Unknown";
  };

  const getStatusBadgeColor = (statusText: string) => {
    const normalized = statusText.trim().toLowerCase();

    switch (normalized) {
      case 'in progress':
      case 'lead':
        return '#6b7280';
      case 'completed':
      case 'confirmed':
        return '#16a34a';
      case 'support':
        return '#4b5563';
      case 'request':
        return '#a1a1aa';
      case 'opportunity':
        return '#111111';
      case 'follow-up':
        return '#2563eb';
      case 'maintenance':
      case 'internal':
        return '#334155';
      case 'lost':
      case 'canceled':
      case 'cancelled':
      case 'rejected':
        return '#52525b';
      case 'unknown':
      default:
        return '#6b7280';
    }
  };

  const getStatusBadgeLabel = (statusText: string) =>
    statusText === 'Unknown' || statusText === 'Status' ? 'Unknown' : statusText;

  return (
    <tr>
      <td style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
        {project.title}
      </td>
      <td style={{ color: 'var(--text-secondary)' }}>
        {project.customerTitle}
      </td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
          <select
            className="form-select form-select-sm"
            value={localCategory ?? ''}
            onChange={(e) => setLocalCategory(e.target.value === '' ? null : parseInt(e.target.value))}
            style={{
              minWidth: '200px'
            }}
          >
            <option value="">Select Category</option>
            {categories.map(category => (
              <option key={category.id} value={category.id}>
                {category.title}
              </option>
            ))}
          </select>
          <button
            className="btn btn-success btn-sm"
            onClick={handleSave}
            disabled={saving || localCategory === project.category}
            style={{ whiteSpace: 'nowrap' }}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </td>
      <td>
        <span
          className="badge contact-type-badge"
          style={{ backgroundColor: getStatusBadgeColor(getStatusText(project.statusId)), color: '#fff' }}
        >
          {getStatusBadgeLabel(getStatusText(project.statusId))}
        </span>
      </td>
      <td>
        <div className="btn-group action-group" role="group">
          <button
            className="btn btn-sm action-btn action-view"
            onClick={() => navigate(`/projects/${project.id}`)}
            title="Details"
          >
            <i className="fa-solid fa-eye" aria-hidden="true" />
          </button>
          <button
            className="btn btn-sm action-btn action-edit"
            onClick={() => navigate(`/projects/edit/${project.id}`)}
            title="Edit"
          >
            <i className="fa-solid fa-pen" aria-hidden="true" />
          </button>
        </div>
      </td>
    </tr>
  );
};

export default ProjectCategories;
